<template>
  <Container>
    <section class="module-default mx-auto min-h-[26rem] max-w-[75rem]">
      <div ref="topOffTheFilter" />
      <!-- FILTER -->
      <div class="justify-between gap-5 md:mb-16 md:flex">
        <Filter
          v-if="Array.isArray(terms) && terms.length > 0"
          :terms="terms"
          :filtered="filtered"
          :button-all-text="translations.event_all_types"
          class="md:w-1/2 lg:w-full"
          @filter="setFilter($event)"
        />

        <div
          class="mb-10 mt-5 flex h-fit items-center space-x-11 text-xl font-medium text-primary-blue md:my-[0.625rem] md:w-1/2 md:justify-end"
        >
          <!-- CHECKBOX -->
          <div class="custom-checkbox -white flex items-center">
            <input
              id="online"
              v-model="filterOnline"
              class="h-5 w-5"
              type="checkbox"
            />
            <label for="online" class="label cursor-pointer pl-3">
              {{ translations.event_online }}
            </label>
          </div>
          <!-- DROPDOWN -->

          <div v-if="organizers" class="relative">
            <select
              v-model="selectedOrganizer"
              class="min-w-[7.5rem] max-w-[10rem] cursor-pointer appearance-none overflow-hidden text-ellipsis whitespace-pre !pr-12 outline-none focus:ring-0 sm:max-w-[15rem]"
              :style="`width: ${
                selectedOrganizer?.title?.length + 3 ||
                translations.event_organizer.length + 3
              }ch`"
            >
              <option value="">{{ translations.event_organizer }}</option>
              <option
                v-for="organizer in organizers"
                :key="organizer.slug"
                :value="organizer"
              >
                {{ organizer.title }}
              </option>
            </select>
            <ArrowSimpleIcon
              stroke-width="2.5"
              class="pointer-events-none absolute right-4 top-1/2 w-4 -translate-y-1/2 text-primary-blue transition-transform"
            />
          </div>
        </div>
      </div>

      <TransitionGroup
        tag="ul"
        name="fade"
        mode="out-in"
        class="relative mx-auto grid max-w-75rem grid-cols-1 gap-24 md:gap-40"
      >
        <li
          v-for="event in Events"
          :key="event.slug"
          class="inset-0 flex transition-all duration-500 ease-in-out"
        >
          <!-- EVENT CARD -->
          <EventCard :data="event" is-detail />
        </li>
      </TransitionGroup>

      <div
        v-show="Array.isArray(Events) && Events.length === 0"
        key="no-elements-found"
        class="w-full"
      >
        <p class="text-center text-xl">{{ translations.filter_empty }}</p>
      </div>

      <!-- PAGINATION -->
      <Pagination
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
        @previous-page="onPaginate(currentPage - 1)"
        @next-page="onPaginate(currentPage + 1)"
        @go-to-page="onPaginate($event)"
      />
    </section>
  </Container>
</template>

<script setup>
import { store } from '/store.js'
import { useUrlSearchParams } from '@vueuse/core'
const { locale } = useI18n()

const route = useRoute()
const params = useUrlSearchParams('history')
const Events = ref(null)
const terms = ref(null)
const organizers = ref(null)
const totalPages = ref(1)
const currentPage = ref(1)
const topOffTheFilter = ref(null)

const filterOnline = ref(JSON.parse(route.query.eventFilterOnline || false))
const selectedOrganizer = ref(
  route.query.eventSelectedOrganizer
    ? JSON.parse(route.query.eventSelectedOrganizer)
    : ''
)

const filtered = ref(null)

filtered.value = route.query.filteredEvents

const translations = computed(() => store.translations)

// FUNCTIONS
async function fetchEvents(page = 1) {
  params.page = page
  const { data } = await useAsyncGql({
    operation: 'Events',
    variables: {
      sort: 'date asc',
      page: parseInt(page),
      limit: 6,
      filter: {
        site: locale,
        event_type: { contains: filtered.value },
        online: { is: filterOnline.value ? filterOnline.value : undefined },
        event_organizer: { contains: selectedOrganizer.value.slug },
        id: { not: store.pageId },
      },
    },
  })

  const payload = data.value?.entries

  if (!payload) {
    console.error('Events could not be fetched')
    return
  }

  totalPages.value = Math.ceil(payload.total / payload.per_page)

  currentPage.value = payload.current_page

  Events.value = payload.data
}

function onPaginate(page) {
  fetchEvents(page)
  topOffTheFilter.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

function setFilter(value) {
  params.filteredEvents = value
  filtered.value = value

  fetchEvents(1)
}

// WATCHERS
watch(filterOnline, (newValue) => {
  params.eventFilterOnline = newValue
  fetchEvents(1)
})
watch(selectedOrganizer, (newValue) => {
  params.eventSelectedOrganizer = JSON.stringify(newValue)
  fetchEvents(1)
})

// INITIAL
const { data } = await useAsyncGql({
  operation: 'EventsAndTerms',
  variables: {
    sort: 'date asc',
    page: parseInt(route.query.page || 1),
    limit: 6,
    filter: {
      site: locale,
      event_type: { contains: filtered.value },
      online: { is: filterOnline.value ? filterOnline.value : undefined },
      event_organizer: { contains: selectedOrganizer.value.slug },
      id: { not: store.pageId },
    },
    filterLocale: {
      site: locale,
    },
  },
})
terms.value = data.value?.event_types?.data

organizers.value = data.value?.event_organizers

const payload = data.value?.entries

if (!payload) {
  console.error('Events could not be fetched')
}

totalPages.value = Math.ceil(payload.total / payload.per_page)

currentPage.value = payload.current_page

Events.value = payload.data
</script>
